import React from 'react';
import { Link } from 'react-router-dom';
import { twMerge } from 'tailwind-merge';
import Button from '../components/Button';
import ConfirmationDialog from '../components/ConfirmationDialog';
import CreateServiceModal from '../components/CreateService/CreateServiceModal';
import ViewFeedback from '../components/CreateService/ViewFeedback';
import DataTableView from '../components/DataTableView2';
import DatePicker from '../components/DatePicker';
import Select from '../components/Select';
import useServicesDashboard from '../hooks/logics/services/use-services-dashboard';
import { formatPrices } from '../utils/helpers';
import { IGetAllServicesDocs } from '../utils/types';

export const serviceTypeMap: { [key: string]: string } = {
  instore_pickup_meta: 'In store',
  curbside_pickup_meta: 'Curbside',
  package_pickup_meta: 'Package',
  all: 'In store pickup, curbside pickup and package',
};

export const serviceStatusOptions = [
  { value: 'unassigned', label: 'Unassigned' },
  { value: 'assigned', label: 'Assigned' },
  { value: 'wayToStore', label: 'Way to store' },
  { value: 'arrivedAtPickup', label: 'Arrived at pickup' },
  { value: 'confirmItems', label: 'Confirm items' },
  { value: 'proceedToDropoff', label: 'Proceed to dropoff' },
  { value: 'arrivedAtDropoff', label: 'Arrived at dropoff' },
  { value: 'confirmDropoff', label: 'Confirm dropoff' },
  { value: 'scheduled', label: 'Scheduled' },
  { value: 'completed', label: 'Completed' },
  { value: 'cancelled', label: 'Cancelled' },
];

export const statusMap = (status: string, className: string) => {
  const map: { [key: string]: { text: string } } = {
    arrivedAtDropoff: { text: 'Arrived at dropoff' },
    unassigned: { text: 'Unassigned' },
    notProcessable: { text: 'Not processable' },
    assigned: { text: 'Assigned' },
    wayToStore: { text: 'Way to store' },
    arrivedAtPickup: { text: 'Arrived at pickup' },
    confirmItems: { text: 'Confirm items' },
    proceedToDropoff: { text: 'Proceed to dropoff' },
    confirmDropoff: { text: 'Confirm dropoff' },
    delivered: { text: 'Delivered' },
    ratedByUser: { text: 'Completed' },
    ratedByDriver: { text: 'Rated by driver' },
    deliver: { text: 'Deliver' },
    completed: { text: 'Completed' },
    cancelled: { text: 'Cancelled' },
    scheduled: { text: 'Scheduled' },
  };
  return <p className={className}>{map[status]?.text || 'N/A'}</p>;
};

const ServicesDashboard = () => {
  const {
    form,
    services,
    totalPage,
    loading,
    isCreatingService,
    setIsCreatingService,
    selectedServiceId,
    setSelectedServiceId,
    selectedFeedback,
    setSelectedFeedback,
    isCancellingService,
    status,
    setStatus,
    type,
    setType,
    fromDate,
    toDate,
    handleFromDate,
    handleToDate,
    filterServices,
    handlePageClick,
    handleServiceCancel,
    resetFilters,
    isApplyButtonDisabled,
    isClearButtonDisabled,
    searchParams,
    refetch,
  } = useServicesDashboard();

  const columns = [
    {
      header: 'Driver',
      className: 'md:p-0 md:pl-10',
      accessor: (service: IGetAllServicesDocs) => (
        <div className='md:!pl-8'>
          {service?.driver?.length === 0 ? (
            <p className='text-md text-black font-normal break-all'>N/A</p>
          ) : (
            <Link to={`/dashboard/drivers/${service?.driver[0]?._id}`}>
              <p className='text-md font-normal text-black break-all max-w-[280px] truncate'>
                {service?.driver[0]?.fullName}
              </p>
            </Link>
          )}
        </div>
      ),
    },
    {
      header: 'User',
      accessor: (service: IGetAllServicesDocs) =>
        service?.user?.length === 0 ? (
          <p className='text-md break-all font-normal text-black'>N/A</p>
        ) : (
          <Link to={`/dashboard/users/${service.user[0]?._id}`}>
            <p className='text-md break-all font-normal text-black'>
              {service?.user[0]?.fullName}
            </p>
          </Link>
        ),
    },
    {
      header: 'Status',
      accessor: (service: IGetAllServicesDocs) => (
        <div
          className={twMerge(
            'text-md font-normal whitespace-nowrap text-orange-600 break-all',
            service?.status === 'cancelled' && 'text-red-600',
            (service?.status === 'ratedByUser' ||
              service?.status === 'completed') &&
            'text-green-600'
          )}
        >
          {statusMap(service.status, '')}
        </div>
      ),
    },
    {
      header: 'User total',
      accessor: (service: IGetAllServicesDocs) =>
        service?.userTotal ? formatPrices(service?.userTotal) : 'N/A',
    },
    {
      header: 'Driver total',
      accessor: (service: IGetAllServicesDocs) =>
        formatPrices(service?.driverTotal),
    },
    {
      header: 'Type',
      accessor: (service: IGetAllServicesDocs) =>
        serviceTypeMap[service?.serviceMetaType],
    },
    {
      header: 'Actions',
      accessor: (service: IGetAllServicesDocs) => (
        <>
          <Link to={`/dashboard/orders/${service?._id}`}>
            <button className='text-primary cursor-pointer'>Details</button>
          </Link>
          {service?.ratingByUser && (
            <button
              onClick={() =>
                setSelectedFeedback({
                  orderId: service?._id,
                  experienceRating: service?.ratingByUser?.rating,
                  feedback: service?.ratingByUser?.feedback,
                })
              }
              className='text-primary ml-4'
            >
              View Feedback
            </button>
          )}
          {service?.status !== 'ratedByUser' &&
            service?.status !== 'cancelled' &&
            service?.status !== 'delivered' && (
              <button
                onClick={() => setSelectedServiceId(service?._id)}
                className='text-red-600 ml-4'
              >
                Cancel
              </button>
            )}
        </>
      ),
    },
  ];

  return (
    <>
      <div className='w-full h-full'>
        <div className='flex flex-col justify-center font-inter bg-gray-100'>
          <div className='w-full flex flex-wrap gap-4 justify-end items-center mb-4'>
            <form
              ref={form}
              onSubmit={filterServices}
              className='flex flex-wrap gap-x-2 justify-end items-center'
            >
              <div className='w-full mb-4 flex items-center justify-end gap-4 md:mt-0'>
                <Button
                  onClick={() => setIsCreatingService(true)}
                  type='button'
                  className='w-full min-h-0 h-10 md:h-12 md:w-auto md:px-3'
                >
                  Create order
                </Button>
              </div>
              <div className='w-full flex flex-wrap items-center justify-end gap-4'>
                <div className='w-full flex flex-row gap-x-2 md:flex-row md:gap-x-4 md:w-max mt-4 md:mt-0'>
                  <div className='flex flex-col items-start md:items-center md:flex-row gap-y-1 w-1/2 md:w-full'>
                    <label className='mr-2'>From:</label>
                    <DatePicker
                      placeholderText='MM/DD/YYYY'
                      onChange={handleFromDate}
                      name='fromDate'
                      selected={fromDate ? new Date(fromDate) : null}
                      className='min-h-0 h-8 md:h-10 py-1'
                      inputClassName='py-0 md:py-2 h-8 md:h-10'
                      popperClassName='!left-7'
                    />
                  </div>
                  <div className='flex flex-col items-start md:items-center md:flex-row gap-y-1 w-1/2 md:w-full'>
                    <label className='mr-2'>To:</label>
                    <DatePicker
                      placeholderText='MM/DD/YYYY'
                      name='toDate'
                      selected={toDate ? new Date(toDate) : null}
                      onChange={handleToDate}
                      className='min-h-0 h-8 md:h-10 py-1'
                      inputClassName='py-0 md:py-2 h-8 md:h-10'
                      popperClassName='!-left-7'
                    />
                  </div>
                </div>
                <div className='w-full md:w-auto flex gap-x-2 md:gap-x-4'>
                  <Select
                    name='status'
                    onChange={(e) => setStatus(e.target.value)}
                    value={status}
                    className='md:w-32 outline-none bg-white rounded-lg min-h-0 h-8 md:h-10 pl-4 pr-2'
                    options={serviceStatusOptions}
                    placeholder='Status'
                    containerClassName='w-1/2 md:w-auto'
                  />
                  <Select
                    name='type'
                    onChange={(e) => setType(e.target.value)}
                    value={type}
                    className='md:w-32 outline-none bg-white rounded-lg min-h-0 h-8 md:h-10 pl-4 pr-2'
                    containerClassName='w-1/2 md:w-auto'
                    options={[
                      { label: 'Package', value: 'package_pickup_meta' },
                      { label: 'Curb', value: 'curbside_pickup_meta' },
                      { label: 'In store', value: 'instore_pickup_meta' },
                    ]}
                    placeholder='Type'
                  />
                </div>
                <div className='w-full md:w-auto flex gap-x-2 md:gap-x-4 mb-8 md:mb-0'>
                  <Button
                    disabled={isApplyButtonDisabled()}
                    type='submit'
                    className='flex-1 md:flex-none md:w-24 min-h-0 h-8 md:h-10'
                  >
                    Apply
                  </Button>
                  <Button
                    disabled={isClearButtonDisabled()}
                    type='button'
                    onClick={resetFilters}
                    className='flex-1 md:flex-none md:w-24 min-h-0 h-8 md:h-10'
                  >
                    Clear
                  </Button>
                </div>
              </div>
            </form>
          </div>

          <DataTableView
            data={services}
            columns={columns}
            isFetching={loading}
            emptyStateMessage='No services found'
            currentPage={parseInt(searchParams.get('page') || '1')}
            setCurrentPage={(page: number) =>
              handlePageClick({ selected: page - 1 })
            }
            totalPages={totalPage}
            height='min-h-[16rem]'
          />
        </div>
      </div>

      <ViewFeedback
        feedback={selectedFeedback}
        isVisible={selectedFeedback.orderId !== ''}
        screen='driverDetails'
        setVisible={() =>
          setSelectedFeedback({
            experienceRating: null,
            feedback: '',
            orderId: '',
          })
        }
      />
      <ConfirmationDialog
        isSubmitButtonDisabled={isCancellingService}
        isSubmitting={isCancellingService}
        handleClick={handleServiceCancel}
        isVisible={selectedServiceId !== ''}
        onHide={() => setSelectedServiceId('')}
        text='Are you sure you want to cancel this service?'
      />
      <CreateServiceModal
        isVisible={isCreatingService}
        onHide={() => setIsCreatingService(false)}
        onServiceCreation={refetch}
      />
    </>
  );
};

export default ServicesDashboard;
